'use client';

import type { BlogCardProps } from '@routeroyal/theme-core';
import Link from 'next/link';
import Image from 'next/image';

/**
 * AllysBlogCard - Allys-themed blog card
 * Standalone implementation with Inter font and Allys navy/gold color scheme
 */
export function AllysBlogCard({
  article,
  variant = 'default',
  showExcerpt = true,
  showCategory = true,
  showAuthor = true,
  locale = 'en',
  className = '',
}: BlogCardProps) {
  const isCompact = variant === 'compact';
  const isFeatured = variant === 'featured';

  return (
    <article 
      className={`
        group relative bg-white rounded-2xl overflow-hidden 
        border border-gray-200
        hover:border-[#BBA339]/30
        hover:shadow-xl
        transition-all duration-300
        [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif]
        ${className}
      `}
    >
      <Link href={`/${locale}/news/${article.slug}`} className="block">
        {/* Featured Image */}
        {article.featuredImage && (
          <div className={`relative ${isFeatured ? 'h-96' : isCompact ? 'h-48' : 'h-64'} overflow-hidden`}>
            <Image
              src={article.featuredImage}
              alt={article.title}
              fill
              className="object-cover group-hover:scale-105 transition-transform duration-300"
              sizes={isFeatured ? '(max-width: 768px) 100vw, 800px' : '(max-width: 768px) 100vw, 400px'}
            />
            {showCategory && article.category && (
              <div className="absolute top-4 left-4">
                <span className="px-3 py-1 text-xs font-semibold text-white bg-[#243462] backdrop-blur-sm rounded-full [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif]">
                  {article.category}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Content */}
        <div className={`p-6 ${isFeatured ? 'lg:p-8' : ''}`}>
          {/* Title */}
          <h3 className={`font-bold mb-3 line-clamp-2 text-[#243462] group-hover:text-[#BBA339] transition-colors [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif] ${
            isFeatured ? 'text-2xl lg:text-3xl' : isCompact ? 'text-lg' : 'text-xl'
          }`}>
            {article.title}
          </h3>

          {/* Excerpt */}
          {showExcerpt && article.excerpt && !isCompact && (
            <p className="text-gray-600 mb-4 line-clamp-3 [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif]">
              {article.excerpt}
            </p>
          )}

          {/* Meta Information */}
          <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500 [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif]">
            {showAuthor && (
              <div className="flex items-center gap-2">
                {article.author.avatar && (
                  <div className="relative w-8 h-8 rounded-full overflow-hidden ring-1 ring-[#BBA339]/20">
                    <Image
                      src={article.author.avatar}
                      alt={article.author.name}
                      fill
                      className="object-cover"
                    />
                  </div>
                )}
                <span className="font-medium text-[#243462]">{article.author.name}</span>
              </div>
            )}
            <span>•</span>
            <time dateTime={article.publishedAt} className="text-gray-600">
              {new Date(article.publishedAt).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </time>
            {article.readTime && (
              <>
                <span>•</span>
                <span className="text-[#243462] font-medium">{article.readTime}</span>
              </>
            )}
          </div>
        </div>
      </Link>
    </article>
  );
}

